// ============================================
// PROYECTOS.JS — Gestión completa de proyectos
// ============================================
import React, { useState, useEffect, useMemo } from 'react';
import { OPC_DINERO, fmtNumDinero } from './utils/dinero';

const PP = "'Poppins', sans-serif";
const COLOR_PRINCIPAL = '#5c9adb';
const COLOR_OSCURO = '#2f5f9e';
const COLOR_FONDO = '#f3f4f4';
const COLOR_GRIS = '#4e5357';
const COLOR_GRIS_CLARO = '#8a8f95';
const COLOR_VERDE = '#5cd4b0';
const COLOR_ROJO = '#e05c5c';
const COLOR_AMARILLO = '#dcde74';

const KEY_PROYECTOS = 'beep_proyectos';
const KEY_USUARIOS = 'beep_usuarios';

const TIPOS_PROYECTO = {
  instalacion:   { label: 'Instalación nueva', icono: '🏗️', color: '#5cd4b0' },
  remodelacion:  { label: 'Remodelación',      icono: '🔄', color: '#5c9adb' },
  mantenimiento: { label: 'Mantenimiento',     icono: '🔩', color: '#f4a261' },
  soporte_ti:    { label: 'Soporte TI',        icono: '💻', color: '#9b59b6' },
  monitoreo:     { label: 'Monitoreo',         icono: '👁️', color: '#2f5f9e' },
  levantamiento: { label: 'Levantamiento',     icono: '📏', color: '#dcde74' },
};

const ESTADOS = {
  nuevo:         { label: 'Nuevo',              color: '#888',    progreso: 5 },
  levantamiento: { label: 'En levantamiento',   color: '#dcde74', progreso: 15 },
  cotizacion:    { label: 'Cotización enviada', color: '#5c9adb', progreso: 25 },
  aprobado:      { label: 'Aprobado',           color: '#5cd4b0', progreso: 35 },
  anticipo:      { label: 'Anticipo recibido',  color: '#5cd4b0', progreso: 45 },
  en_ejecucion:  { label: 'En ejecución',       color: '#f4a261', progreso: 65 },
  completado:    { label: 'Completado',         color: '#2f5f9e', progreso: 85 },
  entregado:     { label: 'Entregado',          color: '#5cd4b0', progreso: 95 },
  facturado:     { label: 'Facturado',          color: '#5cd4b0', progreso: 100 },
  cancelado:     { label: 'Cancelado',          color: '#e05c5c', progreso: 0 },
};

const SUBTIPOS = ['cctv', 'redes', 'alarmas', 'control_acceso', 'computo', 'general'];
const PRIORIDADES = {
  alta:  { label: 'Alta',  color: COLOR_ROJO },
  media: { label: 'Media', color: COLOR_AMARILLO },
  baja:  { label: 'Baja',  color: COLOR_VERDE },
};

function cargarClientesBeep() {
  try { return JSON.parse(localStorage.getItem('beep_clientes') || '[]'); } catch { return []; }
}
function nombresClientesBeep() { return cargarClientesBeep().map(c => c.nombre); }
function sucursalesClienteBeep(nombre) {
  const c = cargarClientesBeep().find(x => x.nombre === nombre);
  return (c?.sucursales || []).map(s => s.nombre);
}

function PROYECTOS_DEMO() {
  const ahora = Date.now();
  return [
    {
      id: 100, folio: 'PRY-2026-100',
      titulo: 'Sistema Integral CCTV Bodegas Alianza Norte',
      tipo: 'instalacion', subtipo: 'cctv', estado: 'en_ejecucion',
      cliente: 'Bodegas Alianza', sucursal: 'Sucursal Norte',
      contacto: '', telefono: '',
      descripcion: 'Instalación de 16 cámaras IP con grabador NVR 4K',
      alcance: 'Cableado, instalación, configuración y capacitación',
      requiereVisita: true, requiereCotizacion: true, requiereAnticipo: true,
      montoTotal: 121270, anticipo80: 97016, finiquito20: 24254,
      anticipoRecibido: true, finiquitoRecibido: false,
      tecnicosAsignados: [{ id: 4, nombre: 'Isaías Méndez' }],
      supervisorAsignado: { id: 4, nombre: 'Isaías Méndez' },
      fechaInicio: new Date(ahora - 5 * 86400000).toISOString(),
      fechaFinEstimada: new Date(ahora + 3 * 86400000).toISOString(),
      fechaFinReal: null,
      tiempoEstimado: '2 semanas',
      personalRecomendado: 'Cuadrilla completa',
      materialesBase: ['16 cámaras IP bala', 'NVR 32 canales', '1200m cable UTP', 'Switch PoE'],
      siguientePaso: 'Instalación de cableado en áreas pendientes',
      prioridad: 'alta',
      fotos: [], documentos: [], notas: [],
      historial: [
        { fecha: new Date(ahora - 10 * 86400000).toISOString(), accion: 'Proyecto creado' },
        { fecha: new Date(ahora -  8 * 86400000).toISOString(), accion: 'Cotización enviada: $121,270' },
        { fecha: new Date(ahora -  7 * 86400000).toISOString(), accion: 'Proyecto aprobado por cliente' },
        { fecha: new Date(ahora -  6 * 86400000).toISOString(), accion: 'Anticipo recibido: $97,016' },
        { fecha: new Date(ahora -  5 * 86400000).toISOString(), accion: 'Inicio de ejecución' },
      ],
      origen: 'manual', origenId: null,
      firmaCliente: null, calificacion: null,
    },
    {
      id: 101, folio: 'PRY-2026-101',
      titulo: 'Red WiFi Farma Plus Polanco',
      tipo: 'instalacion', subtipo: 'redes', estado: 'cotizacion',
      cliente: 'Farma Plus', sucursal: 'Polanco',
      contacto: '', telefono: '',
      descripcion: 'Instalación red WiFi mesh 3 puntos de acceso',
      alcance: 'Suministro e instalación de equipos de red',
      requiereVisita: true, requiereCotizacion: true, requiereAnticipo: true,
      montoTotal: 18500, anticipo80: 14800, finiquito20: 3700,
      anticipoRecibido: false, finiquitoRecibido: false,
      tecnicosAsignados: [], supervisorAsignado: null,
      fechaInicio: null, fechaFinEstimada: null, fechaFinReal: null,
      tiempoEstimado: '3 días', personalRecomendado: 'Técnico',
      materialesBase: ['3 APs mesh', 'Switch 8 puertos', 'Cable UTP'],
      siguientePaso: 'Esperar aprobación del cliente',
      prioridad: 'media',
      fotos: [], documentos: [], notas: [],
      historial: [
        { fecha: new Date(ahora - 3 * 86400000).toISOString(), accion: 'Proyecto creado' },
        { fecha: new Date(ahora - 2 * 86400000).toISOString(), accion: 'Cotización enviada: $18,500' },
      ],
      origen: 'manual', origenId: null,
      firmaCliente: null, calificacion: null,
    },
  ];
}

function cargarProyectos() {
  let extras = [];
  try { extras = JSON.parse(localStorage.getItem(KEY_PROYECTOS) || '[]'); } catch {}
  return [...extras, ...PROYECTOS_DEMO()];
}
function guardarProyectosLS(arr) {
  // Solo guardamos los que no son demo (id < 100 = creados por usuario)
  try {
    const propios = arr.filter(p => p.id < 100 || p.id > 200);
    localStorage.setItem(KEY_PROYECTOS, JSON.stringify(propios));
    window.dispatchEvent(new Event('beep:proyectos-actualizados'));
  } catch {}
}
function cargarUsuarios() {
  try { return JSON.parse(localStorage.getItem(KEY_USUARIOS) || '[]'); } catch { return []; }
}

function iniciales(nombre) {
  return (nombre || '?').split(' ').filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join('');
}
function siguienteFolio(arr) {
  const año = new Date().getFullYear();
  const n = arr.length + 1;
  return `PRY-${año}-${String(n).padStart(3, '0')}`;
}
function fechaCorta(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return '—'; }
}
function calcCobrado(p) {
  let cobrado = 0;
  if (p.anticipoRecibido) cobrado += p.anticipo80 || 0;
  if (p.finiquitoRecibido) cobrado += p.finiquito20 || 0;
  return cobrado;
}

// ============================================
// COMPONENTE PRINCIPAL
// ============================================
function Proyectos({ pestana, rolUsuario, esCelular }) {
  const [proyectos, setProyectos] = useState(cargarProyectos);
  const [usuarios] = useState(cargarUsuarios);
  const [tab, setTab] = useState('activos');
  const [busqueda, setBusqueda] = useState('');
  const [modal, setModal] = useState(null);

  useEffect(() => { guardarProyectosLS(proyectos); }, [proyectos]);

  useEffect(() => {
    const sync = () => setProyectos(cargarProyectos());
    window.addEventListener('beep:proyectos-actualizados', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('beep:proyectos-actualizados', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const tecnicosYSupervisores = useMemo(
    () => usuarios.filter(u => ['tecnico', 'supervisor'].includes(u.rol)),
    [usuarios]
  );

  const filtrados = useMemo(() => {
    let arr = proyectos;
    if (busqueda) {
      const q = busqueda.toLowerCase();
      arr = arr.filter(p =>
        (p.folio || '').toLowerCase().includes(q) ||
        (p.titulo || '').toLowerCase().includes(q) ||
        (p.cliente || '').toLowerCase().includes(q)
      );
    }
    if (tab === 'activos') {
      arr = arr.filter(p => ['aprobado', 'anticipo', 'en_ejecucion'].includes(p.estado));
    } else if (tab === 'pipeline') {
      arr = arr.filter(p => ['nuevo', 'levantamiento', 'cotizacion'].includes(p.estado));
    } else if (tab === 'historial') {
      arr = arr.filter(p => ['completado', 'entregado', 'facturado', 'cancelado'].includes(p.estado));
    }
    return arr;
  }, [proyectos, busqueda, tab]);

  const crearProyecto = (data) => {
    const monto = Number(data.montoTotal) || 0;
    const nuevo = {
      id: Date.now(),
      folio: siguienteFolio(proyectos),
      ...data,
      montoTotal: monto,
      anticipo80: Number((monto * 0.8).toFixed(2)),
      // El finiquito es lo que FALTA: asi anticipo + finiquito suman el total al centavo.
      finiquito20: Number((monto - Number((monto * 0.8).toFixed(2))).toFixed(2)),
      anticipoRecibido: false,
      finiquitoRecibido: false,
      requiereVisita: true,
      requiereCotizacion: true,
      requiereAnticipo: true,
      estado: 'nuevo',
      fechaFinReal: null,
      fotos: [], documentos: [], notas: [],
      historial: [{ fecha: new Date().toISOString(), accion: 'Proyecto creado' }],
      origen: 'manual', origenId: null,
      firmaCliente: null, calificacion: null,
      materialesBase: data.materialesBase || [],
      siguientePaso: data.siguientePaso || 'Definir alcance',
    };
    setProyectos([nuevo, ...proyectos]);
    setModal(null);
  };

  const cambiarEstado = (id, nuevoEstado, accionTxt) => {
    setProyectos(prev => prev.map(p => {
      if (p.id !== id) return p;
      return {
        ...p,
        estado: nuevoEstado,
        historial: [...(p.historial || []), { fecha: new Date().toISOString(), accion: accionTxt || ESTADOS[nuevoEstado]?.label || nuevoEstado }],
      };
    }));
  };

  const actualizar = (id, cambios) => {
    setProyectos(prev => prev.map(p => p.id === id ? { ...p, ...cambios } : p));
  };

  const tarjetaCard = { background: '#fff', borderRadius: 14, padding: esCelular ? 12 : 18, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' };
  const inputBase = { padding: '10px 12px', borderRadius: 10, border: '1px solid #d8dde3', fontFamily: PP, fontSize: 13, outline: 'none', background: '#fff', boxSizing: 'border-box' };
  const btnPrimario = { padding: '10px 18px', background: COLOR_PRINCIPAL, color: '#fff', border: 'none', borderRadius: 10, fontFamily: PP, fontSize: 13, fontWeight: 700, cursor: 'pointer' };

  const tabs = [
    { id: 'activos', label: 'Activos' },
    { id: 'pipeline', label: 'Pipeline' },
    { id: 'historial', label: 'Historial' },
  ];

  return (
    <div style={{ fontFamily: PP, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: esCelular ? 20 : 24, fontWeight: 800, color: COLOR_GRIS }}>📐 Proyectos</div>
          <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginTop: 4 }}>{filtrados.length} en esta vista</div>
        </div>
        <button onClick={() => setModal({ modo: 'crear' })} style={btnPrimario}>+ Nuevo proyecto</button>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: '8px 18px', borderRadius: 12,
              background: tab === t.id ? COLOR_PRINCIPAL : '#fff',
              color: tab === t.id ? '#fff' : COLOR_OSCURO,
              border: `1.5px solid ${COLOR_PRINCIPAL}`,
              fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: PP,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div style={tarjetaCard}>
        <input
          type="text"
          placeholder="🔍 Buscar por folio, título o cliente..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          style={{ ...inputBase, width: '100%' }}
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {filtrados.length === 0 ? (
          <div style={{ ...tarjetaCard, textAlign: 'center', padding: 40, color: COLOR_GRIS_CLARO, fontSize: 13 }}>
            No hay proyectos en esta vista.
          </div>
        ) : filtrados.map(p => (
          <TarjetaProyecto key={p.id} proyecto={p} esCelular={esCelular} onAbrir={() => setModal({ modo: 'detalle', proyecto: p })} />
        ))}
      </div>

      {modal?.modo === 'crear' && (
        <ModalNuevoProyecto
          tecnicos={tecnicosYSupervisores}
          onCrear={crearProyecto}
          onCerrar={() => setModal(null)}
        />
      )}
      {modal?.modo === 'detalle' && (
        <ModalDetalle
          proyecto={proyectos.find(p => p.id === modal.proyecto.id) || modal.proyecto}
          tecnicos={tecnicosYSupervisores}
          onCerrar={() => setModal(null)}
          onCambiarEstado={(estado, txt) => cambiarEstado(modal.proyecto.id, estado, txt)}
          onActualizar={(cambios) => actualizar(modal.proyecto.id, cambios)}
        />
      )}
    </div>
  );
}

// ============================================
// TARJETA DE PROYECTO
// ============================================
function TarjetaProyecto({ proyecto, esCelular, onAbrir }) {
  const tipo = TIPOS_PROYECTO[proyecto.tipo] || TIPOS_PROYECTO.instalacion;
  const estado = ESTADOS[proyecto.estado] || ESTADOS.nuevo;
  const cobrado = calcCobrado(proyecto);
  const pctCobrado = proyecto.montoTotal > 0 ? Math.round((cobrado / proyecto.montoTotal) * 100) : 0;

  return (
    <div
      onClick={onAbrir}
      style={{
        background: '#fff', borderRadius: 12,
        boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
        display: 'flex', overflow: 'hidden', cursor: 'pointer',
      }}
    >
      <div style={{ width: 6, background: tipo.color, flexShrink: 0 }} />
      <div style={{ flex: 1, padding: esCelular ? 12 : 16, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <span style={{ fontSize: 18 }}>{tipo.icono}</span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, fontWeight: 700 }}>{proyecto.folio}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_GRIS, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {proyecto.titulo}
              </div>
            </div>
          </div>
          <span style={{ fontSize: 10, fontWeight: 700, background: estado.color + '22', color: estado.color, padding: '4px 10px', borderRadius: 12 }}>
            {estado.label}
          </span>
        </div>

        <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginBottom: 8 }}>
          {proyecto.cliente}{proyecto.sucursal ? ` — ${proyecto.sucursal}` : ''}
        </div>

        {/* BARRA DE PROGRESO */}
        <div style={{ background: '#eef0f3', height: 6, borderRadius: 4, overflow: 'hidden', marginBottom: 8 }}>
          <div style={{
            width: `${estado.progreso}%`, height: '100%',
            background: tipo.color, transition: 'width 0.4s',
          }} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_OSCURO }}>
              ${(proyecto.montoTotal || 0).toLocaleString('es-MX', OPC_DINERO)}
            </div>
            <div style={{ fontSize: 10, color: COLOR_GRIS_CLARO }}>
              Cobrado: {pctCobrado}% (${cobrado.toLocaleString('es-MX', OPC_DINERO)})
            </div>
          </div>
          {(proyecto.tecnicosAsignados || []).length > 0 && (
            <div style={{ display: 'flex', gap: -6 }}>
              {proyecto.tecnicosAsignados.slice(0, 3).map((t, i) => (
                <div key={i} style={{
                  width: 26, height: 26, borderRadius: '50%',
                  background: COLOR_PRINCIPAL, color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 10, fontWeight: 700, marginLeft: i === 0 ? 0 : -8,
                  border: '2px solid #fff',
                }}>
                  {iniciales(t.nombre)}
                </div>
              ))}
            </div>
          )}
        </div>

        {(proyecto.fechaInicio || proyecto.fechaFinEstimada) && (
          <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 6 }}>
            📅 {fechaCorta(proyecto.fechaInicio)} → {fechaCorta(proyecto.fechaFinEstimada)}
          </div>
        )}
        {proyecto.siguientePaso && (
          <div style={{ fontSize: 11, color: COLOR_OSCURO, marginTop: 4, fontStyle: 'italic' }}>
            👉 {proyecto.siguientePaso}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// MODAL NUEVO PROYECTO (3 pasos)
// ============================================
function ModalNuevoProyecto({ tecnicos, onCrear, onCerrar }) {
  const [paso, setPaso] = useState(1);
  const [tipo, setTipo] = useState('instalacion');
  const [subtipo, setSubtipo] = useState('general');
  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [alcance, setAlcance] = useState('');
  const [prioridad, setPrioridad] = useState('media');
  const [cliente, setCliente] = useState('');
  const [busquedaCliente, setBusquedaCliente] = useState('');
  const [sucursal, setSucursal] = useState('');
  const [tecnicosSel, setTecnicosSel] = useState([]);
  const [supervisorId, setSupervisorId] = useState('');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFinEstimada, setFechaFinEstimada] = useState('');
  const [montoTotal, setMontoTotal] = useState(0);
  const [tiempoEstimado, setTiempoEstimado] = useState('');

  const sucursalesDisp = cliente ? sucursalesClienteBeep(cliente) : [];
  const clientesFilt = busquedaCliente
    ? nombresClientesBeep().filter(c => c.toLowerCase().includes(busquedaCliente.toLowerCase()))
    : [];

  const inputBase = { padding: '10px 12px', borderRadius: 10, border: '1px solid #d8dde3', fontFamily: PP, fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box' };
  const labelStyle = { fontSize: 11, color: COLOR_GRIS_CLARO, fontWeight: 700, marginBottom: 4, display: 'block', textTransform: 'uppercase' };

  const puede1 = tipo && titulo.trim();
  const puede2 = cliente;

  const toggleTec = (id) => {
    setTecnicosSel(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const submit = () => {
    const supervisor = tecnicos.find(t => t.id === supervisorId);
    onCrear({
      tipo, subtipo, titulo: titulo.trim(),
      descripcion: descripcion.trim(), alcance: alcance.trim(),
      prioridad,
      cliente, sucursal, contacto: '', telefono: '',
      tecnicosAsignados: tecnicosSel.map(id => {
        const t = tecnicos.find(x => x.id === id);
        return { id: t.id, nombre: t.nombre };
      }),
      supervisorAsignado: supervisor ? { id: supervisor.id, nombre: supervisor.nombre } : null,
      fechaInicio: fechaInicio ? new Date(fechaInicio).toISOString() : null,
      fechaFinEstimada: fechaFinEstimada ? new Date(fechaFinEstimada).toISOString() : null,
      montoTotal,
      tiempoEstimado,
      personalRecomendado: tecnicosSel.length > 1 ? 'Cuadrilla completa' : 'Técnico',
      materialesBase: [],
      siguientePaso: 'Definir alcance y agendar levantamiento',
    });
  };

  return (
    <div style={overlay}>
      <div style={{ ...modalCard, maxWidth: 520 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: COLOR_OSCURO }}>Nuevo proyecto</div>
          <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO }}>Paso {paso} de 3</div>
        </div>

        <div style={{ display: 'flex', gap: 4, marginBottom: 18 }}>
          {[1, 2, 3].map(n => (
            <div key={n} style={{ flex: 1, height: 4, borderRadius: 4, background: n <= paso ? COLOR_PRINCIPAL : '#e6e9ee' }} />
          ))}
        </div>

        {paso === 1 && (
          <div>
            <label style={labelStyle}>Tipo de proyecto</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
              {Object.entries(TIPOS_PROYECTO).map(([k, info]) => (
                <button
                  key={k}
                  onClick={() => setTipo(k)}
                  style={{
                    padding: 12, borderRadius: 10,
                    background: tipo === k ? info.color : '#fff',
                    color: tipo === k ? '#fff' : COLOR_GRIS,
                    border: `1.5px solid ${info.color}`,
                    fontWeight: 700, fontSize: 11, cursor: 'pointer', fontFamily: PP,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                  }}
                >
                  <span style={{ fontSize: 20 }}>{info.icono}</span>
                  {info.label}
                </button>
              ))}
            </div>

            <label style={{ ...labelStyle, marginTop: 12 }}>Subtipo</label>
            <select value={subtipo} onChange={(e) => setSubtipo(e.target.value)} style={inputBase}>
              {SUBTIPOS.map(s => <option key={s} value={s}>{s.replace('_', ' ').toUpperCase()}</option>)}
            </select>

            <label style={{ ...labelStyle, marginTop: 12 }}>Título del proyecto *</label>
            <input value={titulo} onChange={(e) => setTitulo(e.target.value)} style={inputBase} placeholder="Ej: Sistema CCTV oficina central" />

            <label style={{ ...labelStyle, marginTop: 12 }}>Descripción</label>
            <textarea value={descripcion} onChange={(e) => setDescripcion(e.target.value)} rows={2} style={{ ...inputBase, fontFamily: PP, resize: 'vertical' }} />

            <label style={{ ...labelStyle, marginTop: 12 }}>Alcance</label>
            <textarea value={alcance} onChange={(e) => setAlcance(e.target.value)} rows={2} style={{ ...inputBase, fontFamily: PP, resize: 'vertical' }} placeholder="Suministro, instalación, garantía..." />

            <label style={{ ...labelStyle, marginTop: 12 }}>Prioridad</label>
            <div style={{ display: 'flex', gap: 6 }}>
              {Object.entries(PRIORIDADES).map(([k, info]) => (
                <button
                  key={k}
                  onClick={() => setPrioridad(k)}
                  style={{
                    flex: 1, padding: 10, borderRadius: 10,
                    background: prioridad === k ? info.color : '#fff',
                    color: prioridad === k ? '#fff' : COLOR_GRIS,
                    border: `1.5px solid ${info.color}`,
                    fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: PP,
                  }}
                >
                  {info.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {paso === 2 && (
          <div>
            <label style={labelStyle}>Cliente *</label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Buscar cliente..."
                value={cliente || busquedaCliente}
                onChange={(e) => { setCliente(''); setSucursal(''); setBusquedaCliente(e.target.value); }}
                style={inputBase}
              />
              {clientesFilt.length > 0 && !cliente && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0,
                  background: '#fff', border: `1px solid ${COLOR_PRINCIPAL}55`,
                  borderRadius: 8, marginTop: 2, zIndex: 5,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                }}>
                  {clientesFilt.map(c => (
                    <div
                      key={c}
                      onClick={() => { setCliente(c); setBusquedaCliente(''); setSucursal(''); }}
                      style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                    >
                      {c}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {cliente && sucursalesDisp.length > 0 && (
              <>
                <label style={{ ...labelStyle, marginTop: 12 }}>Sucursal</label>
                <select value={sucursal} onChange={(e) => setSucursal(e.target.value)} style={inputBase}>
                  <option value="">Selecciona...</option>
                  {sucursalesDisp.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </>
            )}

            <label style={{ ...labelStyle, marginTop: 12 }}>Técnicos asignados</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {tecnicos.length === 0 && <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO }}>No hay técnicos disponibles</div>}
              {tecnicos.map(t => (
                <button
                  key={t.id}
                  onClick={() => toggleTec(t.id)}
                  style={{
                    padding: '6px 12px', borderRadius: 14,
                    background: tecnicosSel.includes(t.id) ? COLOR_PRINCIPAL : '#fff',
                    color: tecnicosSel.includes(t.id) ? '#fff' : COLOR_OSCURO,
                    border: `1.5px solid ${COLOR_PRINCIPAL}`,
                    fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: PP,
                  }}
                >
                  {t.nombre.split(' ')[0]}
                </button>
              ))}
            </div>

            <label style={{ ...labelStyle, marginTop: 12 }}>Supervisor</label>
            <select value={supervisorId} onChange={(e) => setSupervisorId(Number(e.target.value))} style={inputBase}>
              <option value="">Sin asignar</option>
              {tecnicos.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
            </select>

            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Inicio estimado</label>
                <input type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} style={inputBase} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Fin estimado</label>
                <input type="date" value={fechaFinEstimada} onChange={(e) => setFechaFinEstimada(e.target.value)} style={inputBase} />
              </div>
            </div>

            <label style={{ ...labelStyle, marginTop: 12 }}>Tiempo estimado</label>
            <input value={tiempoEstimado} onChange={(e) => setTiempoEstimado(e.target.value)} style={inputBase} placeholder="Ej: 2 semanas" />

            <label style={{ ...labelStyle, marginTop: 12 }}>Monto total (MXN)</label>
            <input type="number" min="0" value={montoTotal} onChange={(e) => setMontoTotal(Number(e.target.value))} style={inputBase} />
            {montoTotal > 0 && (
              <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 4 }}>
                Anticipo 80%: ${fmtNumDinero(montoTotal * 0.8)} · Finiquito 20%: ${fmtNumDinero(montoTotal * 0.2)}
              </div>
            )}
          </div>
        )}

        {paso === 3 && (
          <div style={{ background: COLOR_FONDO, borderRadius: 12, padding: 16, fontSize: 12, color: COLOR_GRIS, lineHeight: 1.8 }}>
            <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, fontWeight: 700, marginBottom: 6 }}>RESUMEN</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: COLOR_OSCURO, marginBottom: 8 }}>{titulo}</div>
            <div><strong>Tipo:</strong> {TIPOS_PROYECTO[tipo].icono} {TIPOS_PROYECTO[tipo].label} ({subtipo})</div>
            <div><strong>Cliente:</strong> {cliente}{sucursal ? ` — ${sucursal}` : ''}</div>
            {descripcion && <div><strong>Descripción:</strong> {descripcion}</div>}
            <div><strong>Prioridad:</strong> <span style={{ color: PRIORIDADES[prioridad].color, fontWeight: 700 }}>{PRIORIDADES[prioridad].label}</span></div>
            <div><strong>Técnicos:</strong> {tecnicosSel.length > 0 ? tecnicosSel.map(id => tecnicos.find(t => t.id === id)?.nombre).join(', ') : 'Sin asignar'}</div>
            {fechaInicio && <div><strong>Inicio:</strong> {fechaInicio} → {fechaFinEstimada}</div>}
            {tiempoEstimado && <div><strong>Tiempo estimado:</strong> {tiempoEstimado}</div>}
            {montoTotal > 0 && (
              <>
                <div><strong>Monto total:</strong> ${montoTotal.toLocaleString('es-MX', OPC_DINERO)}</div>
                <div><strong>Anticipo (80%):</strong> ${fmtNumDinero(montoTotal * 0.8)}</div>
                <div><strong>Finiquito (20%):</strong> ${fmtNumDinero(montoTotal * 0.2)}</div>
              </>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
          {paso > 1 && (
            <button onClick={() => setPaso(paso - 1)} style={{ flex: 1, padding: 12, background: '#f1f3f6', border: 'none', borderRadius: 10, fontFamily: PP, fontWeight: 700, cursor: 'pointer' }}>
              Atrás
            </button>
          )}
          <button onClick={onCerrar} style={{ flex: 1, padding: 12, background: '#f1f3f6', border: 'none', borderRadius: 10, fontFamily: PP, fontWeight: 700, cursor: 'pointer' }}>
            Cancelar
          </button>
          {paso < 3 ? (
            <button
              onClick={() => setPaso(paso + 1)}
              disabled={paso === 1 ? !puede1 : !puede2}
              style={{
                flex: 1, padding: 12, background: COLOR_PRINCIPAL, color: '#fff',
                border: 'none', borderRadius: 10, fontFamily: PP, fontWeight: 700,
                cursor: 'pointer', opacity: (paso === 1 ? puede1 : puede2) ? 1 : 0.5,
              }}
            >
              Siguiente →
            </button>
          ) : (
            <button onClick={submit} style={{ flex: 1, padding: 12, background: '#1D9E75', color: '#fff', border: 'none', borderRadius: 10, fontFamily: PP, fontWeight: 700, cursor: 'pointer' }}>
              Crear proyecto
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// MODAL DETALLE
// ============================================
function ModalDetalle({ proyecto, tecnicos, onCerrar, onCambiarEstado, onActualizar }) {
  const [siguientePaso, setSiguientePaso] = useState(proyecto.siguientePaso || '');
  const [nuevaNota, setNuevaNota] = useState('');
  const tipo = TIPOS_PROYECTO[proyecto.tipo] || TIPOS_PROYECTO.instalacion;
  const estado = ESTADOS[proyecto.estado] || ESTADOS.nuevo;
  const cobrado = calcCobrado(proyecto);
  const pctCobrado = proyecto.montoTotal > 0 ? Math.round((cobrado / proyecto.montoTotal) * 100) : 0;

  const btn = (bg, color = '#fff') => ({
    padding: '12px 14px', background: bg, color, border: 'none',
    borderRadius: 10, fontFamily: PP, fontWeight: 700, fontSize: 12, cursor: 'pointer', flex: 1,
  });

  const completar = () => {
    if (!nuevaNota.trim() && !proyecto.notas?.length) {
      const r = window.prompt('Escribe un breve reporte de cierre:');
      if (!r || !r.trim()) return;
      onActualizar({ notas: [...(proyecto.notas || []), { texto: r.trim(), fecha: new Date().toISOString() }] });
    }
    onCambiarEstado('completado', 'Proyecto completado');
  };

  const agregarNota = () => {
    if (!nuevaNota.trim()) return;
    onActualizar({ notas: [...(proyecto.notas || []), { texto: nuevaNota.trim(), fecha: new Date().toISOString() }] });
    setNuevaNota('');
  };

  return (
    <div style={overlay}>
      <div style={{ ...modalCard, maxWidth: 600 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 22 }}>{tipo.icono}</span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, fontWeight: 700 }}>{proyecto.folio}</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: COLOR_OSCURO }}>{proyecto.titulo}</div>
                <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO }}>{proyecto.cliente}{proyecto.sucursal ? ` — ${proyecto.sucursal}` : ''}</div>
              </div>
            </div>
          </div>
          <button onClick={onCerrar} style={{ background: 'transparent', border: 'none', fontSize: 24, cursor: 'pointer', color: COLOR_GRIS_CLARO, lineHeight: 1 }}>×</button>
        </div>

        <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 700, background: tipo.color + '22', color: tipo.color, padding: '4px 10px', borderRadius: 12 }}>
            {tipo.label}
          </span>
          <span style={{ fontSize: 11, fontWeight: 700, background: estado.color + '22', color: estado.color, padding: '4px 10px', borderRadius: 12 }}>
            {estado.label}
          </span>
          {proyecto.prioridad && PRIORIDADES[proyecto.prioridad] && (
            <span style={{ fontSize: 11, fontWeight: 700, background: PRIORIDADES[proyecto.prioridad].color + '22', color: PRIORIDADES[proyecto.prioridad].color, padding: '4px 10px', borderRadius: 12 }}>
              {PRIORIDADES[proyecto.prioridad].label}
            </span>
          )}
        </div>

        {/* PROGRESO */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: COLOR_GRIS_CLARO, fontWeight: 700, marginBottom: 4 }}>
            <span>PROGRESO DEL PROYECTO</span>
            <span>{estado.progreso}%</span>
          </div>
          <div style={{ background: '#eef0f3', height: 10, borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ width: `${estado.progreso}%`, height: '100%', background: tipo.color, transition: 'width 0.4s' }} />
          </div>
        </div>

        {/* FINANZAS */}
        {proyecto.montoTotal > 0 && (
          <div style={{ background: COLOR_FONDO, borderRadius: 12, padding: 14, marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, fontWeight: 700, marginBottom: 8 }}>FINANZAS</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 12, color: COLOR_GRIS }}>Monto total</span>
              <span style={{ fontSize: 14, fontWeight: 800, color: COLOR_OSCURO }}>${proyecto.montoTotal.toLocaleString('es-MX', OPC_DINERO)}</span>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: COLOR_GRIS, marginBottom: 4, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!proyecto.anticipoRecibido}
                onChange={(e) => onActualizar({ anticipoRecibido: e.target.checked })}
              />
              Anticipo (80%) recibido — ${proyecto.anticipo80?.toLocaleString('es-MX', OPC_DINERO)}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: COLOR_GRIS, marginBottom: 4, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!proyecto.finiquitoRecibido}
                onChange={(e) => onActualizar({ finiquitoRecibido: e.target.checked })}
              />
              Finiquito (20%) recibido — ${proyecto.finiquito20?.toLocaleString('es-MX', OPC_DINERO)}
            </label>
            <div style={{ fontSize: 11, color: COLOR_OSCURO, fontWeight: 700, marginTop: 4 }}>
              Cobrado: {pctCobrado}% (${cobrado.toLocaleString('es-MX', OPC_DINERO)})
            </div>
          </div>
        )}

        {/* EQUIPO Y FECHAS */}
        <div style={{ background: '#fff', border: `1px solid ${COLOR_FONDO}`, borderRadius: 12, padding: 14, marginBottom: 12, fontSize: 12, color: COLOR_GRIS, lineHeight: 1.8 }}>
          {proyecto.descripcion && <div><strong>Descripción:</strong> {proyecto.descripcion}</div>}
          {proyecto.alcance && <div><strong>Alcance:</strong> {proyecto.alcance}</div>}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
            <strong>Equipo:</strong>
            {(proyecto.tecnicosAsignados || []).length === 0 ? (
              <span style={{ color: COLOR_GRIS_CLARO }}>Sin asignar</span>
            ) : (
              <div style={{ display: 'flex' }}>
                {proyecto.tecnicosAsignados.map((t, i) => (
                  <div key={i} title={t.nombre} style={{
                    width: 26, height: 26, borderRadius: '50%',
                    background: COLOR_PRINCIPAL, color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 10, fontWeight: 700, marginLeft: i === 0 ? 0 : -8,
                    border: '2px solid #fff',
                  }}>
                    {iniciales(t.nombre)}
                  </div>
                ))}
              </div>
            )}
          </div>
          {proyecto.supervisorAsignado && <div><strong>Supervisor:</strong> {proyecto.supervisorAsignado.nombre}</div>}
          {(proyecto.fechaInicio || proyecto.fechaFinEstimada) && (
            <div><strong>Fechas:</strong> {fechaCorta(proyecto.fechaInicio)} → {fechaCorta(proyecto.fechaFinEstimada)}</div>
          )}
          {proyecto.tiempoEstimado && <div><strong>Tiempo estimado:</strong> {proyecto.tiempoEstimado}</div>}
        </div>

        {/* SIGUIENTE PASO */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: COLOR_GRIS_CLARO, marginBottom: 4 }}>SIGUIENTE PASO</div>
          <input
            value={siguientePaso}
            onChange={(e) => setSiguientePaso(e.target.value)}
            onBlur={() => onActualizar({ siguientePaso })}
            style={{ width: '100%', padding: 10, borderRadius: 10, border: `1px solid ${COLOR_PRINCIPAL}55`, fontFamily: PP, fontSize: 13, boxSizing: 'border-box' }}
          />
        </div>

        {/* MATERIALES */}
        {(proyecto.materialesBase || []).length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: COLOR_GRIS_CLARO, marginBottom: 6 }}>MATERIALES BASE</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {proyecto.materialesBase.map((m, i) => (
                <div key={i} style={{ background: '#eaf2fb', padding: '6px 10px', borderRadius: 8, fontSize: 12, color: COLOR_GRIS }}>
                  • {m}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* HISTORIAL */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: COLOR_GRIS_CLARO, marginBottom: 6 }}>LÍNEA DE TIEMPO</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 160, overflowY: 'auto' }}>
            {(proyecto.historial || []).map((h, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, fontSize: 12 }}>
                <span style={{ color: COLOR_PRINCIPAL }}>•</span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: COLOR_GRIS, fontWeight: 600 }}>{h.accion}</div>
                  <div style={{ fontSize: 10, color: COLOR_GRIS_CLARO }}>{new Date(h.fecha).toLocaleString('es-MX')}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* NOTAS */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: COLOR_GRIS_CLARO, marginBottom: 6 }}>NOTAS</div>
          {(proyecto.notas || []).length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 6 }}>
              {proyecto.notas.map((n, i) => (
                <div key={i} style={{ background: '#eaf2fb', borderRadius: 8, padding: 8, fontSize: 12, color: COLOR_GRIS }}>
                  <div>{n.texto}</div>
                  <div style={{ fontSize: 10, color: COLOR_GRIS_CLARO }}>{new Date(n.fecha).toLocaleString('es-MX')}</div>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              value={nuevaNota}
              onChange={(e) => setNuevaNota(e.target.value)}
              placeholder="Agregar nota..."
              style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid #d8dde3', fontFamily: PP, fontSize: 12 }}
            />
            <button onClick={agregarNota} style={{ padding: '8px 14px', background: COLOR_PRINCIPAL, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontFamily: PP }}>+</button>
          </div>
        </div>

        {/* ACCIONES DE ESTADO */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {proyecto.estado === 'nuevo' && (
            <>
              <button onClick={() => onCambiarEstado('levantamiento', 'Levantamiento iniciado')} style={btn(COLOR_AMARILLO, COLOR_GRIS)}>📏 Iniciar levantamiento</button>
              <button onClick={() => onCambiarEstado('cotizacion', 'Cotización creada')} style={btn(COLOR_PRINCIPAL)}>📝 Crear cotización</button>
            </>
          )}
          {proyecto.estado === 'levantamiento' && (
            <button onClick={() => onCambiarEstado('cotizacion', 'Cotización enviada al cliente')} style={btn(COLOR_PRINCIPAL)}>📤 Enviar cotización</button>
          )}
          {proyecto.estado === 'cotizacion' && (
            <button onClick={() => onCambiarEstado('aprobado', 'Proyecto aprobado por cliente')} style={btn(COLOR_VERDE)}>✓ Marcar como aprobado</button>
          )}
          {proyecto.estado === 'aprobado' && (
            <button
              onClick={() => { onActualizar({ anticipoRecibido: true }); onCambiarEstado('anticipo', `Anticipo recibido: $${(proyecto.anticipo80 || 0).toLocaleString('es-MX', OPC_DINERO)}`); }}
              style={btn(COLOR_VERDE)}
            >💰 Registrar anticipo</button>
          )}
          {proyecto.estado === 'anticipo' && (
            <button onClick={() => onCambiarEstado('en_ejecucion', 'Inicio de ejecución')} style={btn('#f4a261')}>▶ Iniciar ejecución</button>
          )}
          {proyecto.estado === 'en_ejecucion' && (
            <button onClick={completar} style={btn('#1D9E75')}>✓ Completar proyecto</button>
          )}
          {proyecto.estado === 'completado' && (
            <button onClick={() => onCambiarEstado('entregado', 'Entregado y firmado')} style={btn(COLOR_VERDE)}>✍️ Registrar entrega</button>
          )}
          {proyecto.estado === 'entregado' && (
            <button
              onClick={() => { onActualizar({ finiquitoRecibido: true }); onCambiarEstado('facturado', `Facturado: $${(proyecto.montoTotal || 0).toLocaleString('es-MX', OPC_DINERO)}`); }}
              style={btn(COLOR_OSCURO)}
            >💵 Marcar facturado</button>
          )}
        </div>
      </div>
    </div>
  );
}

const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(20,30,50,0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000, padding: 16,
};
const modalCard = {
  background: '#fff', borderRadius: 16, padding: 22,
  width: '100%', maxHeight: 'calc(var(--beep-vh) * 92)', overflowY: 'auto',
  boxShadow: '0 20px 60px rgba(0,0,0,0.25)', fontFamily: PP,
};

export default Proyectos;
